import { Box, Sheet, IconButton, Typography, Divider, CircularProgress } from '@mui/joy'
import { CloseFullscreen } from '@mui/icons-material'
import { AICallControls, AICallHistory, AITranscriptionPanel, CallInfo, LoadDetails } from '../../widget-subcomponents'
import { CallStatus, VoiceTranscript } from '@numeo/types'
import { getMaximizedCallOverlayAnimationStyles, maximizedUIAnimationStyles } from '../../../utils/styles'
import { useSearchParams } from 'react-router-dom'
import { useAppSelector } from 'hooks'

interface MaximizedAICallWidgetProps {
    callId: string
    takenOver: boolean
    loadingTakeOver: boolean
    isListening: boolean
    isMinimizing: boolean
    transcriptions: VoiceTranscript[]
    endingCall: boolean

    onTakeOver: () => Promise<void>
    onEndCall: () => Promise<void>
    toggleListening: () => void
    onMinimize: () => void
}

export const MaximizedAICallWidget = ({
    callId,
    takenOver,
    loadingTakeOver,
    isListening,
    isMinimizing,
    transcriptions,
    endingCall,

    onTakeOver,
    onEndCall,
    toggleListening,
    onMinimize,
}: MaximizedAICallWidgetProps) => {
    const [searchParams] = useSearchParams()
    const { activeAvailableCalls } = useAppSelector((state) => state.callCenter)
    const activeAvailableCall = activeAvailableCalls[callId]

    return (
        <>
            <Box sx={getMaximizedCallOverlayAnimationStyles(isMinimizing)} onClick={onMinimize} />
            <Sheet
                sx={{
                    ...maximizedUIAnimationStyles,
                    animation: 'expandFromBottom 0.3s ease-out forwards',
                    border: '1px solid',
                    borderColor: 'divider',
                    pl: 2,
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 2,
                        opacity: isMinimizing ? 0.5 : 1,
                        transition: 'opacity 0.2s ease-in-out',
                    }}
                >
                    <IconButton
                        onClick={onMinimize}
                        variant="plain"
                        sx={{
                            mr: 1,
                            transition: 'transform 0.2s ease-in-out',
                            '&:hover': {
                                transform: 'scale(1.1)',
                                backgroundColor: 'primary.50',
                            },
                        }}
                    >
                        <CloseFullscreen />
                    </IconButton>
                    <Typography
                        level="h4"
                        sx={{
                            ml: 1,
                            color: 'primary.500',
                            opacity: isMinimizing ? 0 : 1,
                            transform: isMinimizing ? 'translateX(-10px)' : 'translateX(0)',
                            transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
                        }}
                    >
                        AI Call
                    </Typography>
                </Box>

                {/* Main Content */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 2,
                        mb: 2,
                        '& > *': {
                            animation: 'slideInContent 0.4s ease-out forwards',
                            opacity: 0,
                        },
                        '& > *:nth-of-type(1)': {
                            animationDelay: '0.1s',
                        },
                        '& > *:nth-of-type(2)': {
                            animationDelay: '0.2s',
                        },
                        '@keyframes slideInContent': {
                            from: {
                                opacity: 0,
                                transform: 'translateY(20px) scale(0.95)',
                            },
                            to: {
                                opacity: 1,
                                transform: 'translateY(0) scale(1)',
                            },
                        },
                    }}
                >
                    {/* Left Column - Call Info & Controls */}
                    <Box>
                        <CallInfo to={activeAvailableCall.phoneNumber} from={activeAvailableCall.call?.from || 'Loading...'} call={activeAvailableCall.call} />

                        <AICallControls
                            onStopCall={onEndCall}
                            onTakeOver={onTakeOver}
                            takingOver={loadingTakeOver}
                            takenOver={takenOver}
                            onToggleListen={toggleListening}
                            isListening={isListening}
                            callStatus={activeAvailableCall.call?.status || CallStatus.INITIATING}
                            endingCall={endingCall}
                        />

                        {/* Status indicators */}
                        {takenOver && (
                            <Box
                                sx={{
                                    mt: 2,
                                    p: 1,
                                    bgcolor: 'success.100',
                                    borderRadius: 'sm',
                                    borderLeft: '4px solid',
                                    borderColor: 'success.500',
                                }}
                            >
                                <Typography level="body-sm" color="success" sx={{ fontWeight: 'medium' }}>
                                    ✅ Call control transferred to you
                                </Typography>
                            </Box>
                        )}

                        {isListening && (
                            <Box
                                sx={{
                                    mt: 2,
                                    p: 1,
                                    bgcolor: 'warning.100',
                                    borderRadius: 'sm',
                                    borderLeft: '4px solid',
                                    borderColor: 'warning.500',
                                }}
                            >
                                <Typography level="body-sm" color="warning" sx={{ fontWeight: 'medium' }}>
                                    🎧 You are listening to the call
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Right Column - Live Transcription */}
                    <Box>
                        <AITranscriptionPanel
                            activeCall={{
                                callId,
                                from: 'AI Assistant',
                                to: activeAvailableCall.phoneNumber,
                                status: activeAvailableCall.call?.status || CallStatus.INITIATING,
                                transcripts: transcriptions,
                                startedAt: activeAvailableCall.call?.startedAt,
                                loadId: activeAvailableCall.load?._id,
                            }}
                            isLive={activeAvailableCall.call?.status === CallStatus.CONNECTED}
                        />
                    </Box>
                </Box>

                {/* Additional sections */}
                <Box
                    sx={{
                        '& > *': {
                            animation: 'fadeInUp 0.5s ease-out forwards',
                            opacity: 0,
                        },
                        '& > *:nth-of-type(1)': { animationDelay: '0.3s' },
                        '& > *:nth-of-type(2)': { animationDelay: '0.4s' },
                        '& > *:nth-of-type(3)': { animationDelay: '0.5s' },
                        '@keyframes fadeInUp': {
                            from: {
                                opacity: 0,
                                transform: 'translateY(15px)',
                            },
                            to: {
                                opacity: 1,
                                transform: 'translateY(0)',
                            },
                        },
                    }}
                >
                    <Divider sx={{ my: 2 }} />

                    {/* Call History */}
                    <AICallHistory callHistory={activeAvailableCall.load.callHistory || []} activeCallId={callId} fullHeight={false} />

                    <Divider sx={{ my: 2 }} />

                    {/* Load Details */}
                    {activeAvailableCall?.load ? (
                        <LoadDetails load={activeAvailableCall.load} truckId={searchParams.get('truckId') || ''} />
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                            <CircularProgress size="sm" />
                        </Box>
                    )}
                </Box>
            </Sheet>
        </>
    )
}
